import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Modal,
  Alert,
  Tabs,
  Tab,
  Badge,
  ListGroup,
  Stack,
} from "react-bootstrap";
import api, { getAuthMode } from "../services/api";
import AdminPolls from "./AdminPolls";
import EmployeeForm106 from "./EmployeeForm106";
import EmployeeWorklog from "./EmployeeWorklog";
import BackgroundJobsBar from "./BackgroundJobsBar";
import AdminLogs from "./AdminLogs";
import TwoFASetupModal from "./TwoFASetupModal";
import TwoFAButton from "./TwoFAButton";
import TwoFADisableModal from "./TwoFADisableModal";
import MassDeleteModal from "./modals/MassDeleteModal";
import BatchUploadModal from "./modals/BatchUploadModal";
import Form106UploadModal from "./modals/Form106UploadModal";
import WorklogUploadModal from "./modals/WorklogUploadModal";
import { useUsers } from "./hooks/useUsers";
import { usePublications } from "./hooks/usePublications";
import { User, Payslip, Publication } from "./types/admin.types";
import { useLanguage } from "../i18n/LanguageContext";

// MONTHS is now built inside the component using i18n

const AdminDashboard: React.FC = () => {
  const { t } = useLanguage();
  const MONTHS = [
    { value: "01", label: t("january") },
    { value: "02", label: t("february") },
    { value: "03", label: t("march") },
    { value: "04", label: t("april") },
    { value: "05", label: t("may") },
    { value: "06", label: t("june") },
    { value: "07", label: t("july") },
    { value: "08", label: t("august") },
    { value: "09", label: t("september") },
    { value: "10", label: t("october") },
    { value: "11", label: t("november") },
    { value: "12", label: t("december") },
  ];
  const formatMonth = (m: string) =>
    MONTHS.find((x) => x.value === m)?.label ?? m;
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [authMode, setAuthMode] = useState<"password" | "authenticator">(
    "password",
  );

  const [showMassDeleteModal, setShowMassDeleteModal] = useState(false);
  const [showBatchUploadModal, setShowBatchUploadModal] = useState(false); // used by BatchUploadModal
  const [showForm106UploadModal, setShowForm106UploadModal] = useState(false);
  const [showWorklogUploadModal, setShowWorklogUploadModal] = useState(false);
  const [worklogPublications, setWorklogPublications] = useState<any[]>([]);
  const [selectedWorklogPub, setSelectedWorklogPub] = useState<any>(null);
  const [showWorklogDetails, setShowWorklogDetails] = useState(false);
  const [deletingWorklogPubId, setDeletingWorklogPubId] = useState<
    string | null
  >(null);
  const [revealedPasswords, setRevealedPasswords] = useState<
    Record<number, string | null>
  >({});
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const copyToClipboard = (text: string, userId: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(userId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };
  const [resettingPasswordId, setResettingPasswordId] = useState<number | null>(
    null,
  );
  const [alertCount, setAlertCount] = useState(0);
  const [form106Publications, setForm106Publications] = useState<any[]>([]);
  const [selectedForm106Pub, setSelectedForm106Pub] = useState<any>(null);
  const [showForm106Details, setShowForm106Details] = useState(false);
  const [deletingForm106PubId, setDeletingForm106PubId] = useState<
    string | null
  >(null);
  const [showBatchUploadEmployeesModal, setShowBatchUploadEmployeesModal] =
    useState(false);
  const [showEmployeeUploadResults, setShowEmployeeUploadResults] =
    useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);

  const [employeeUploadResults, setEmployeeUploadResults] = useState<any>(null);
  const [batchEmployeeFile, setBatchEmployeeFile] = useState<File | null>(null);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    id: "",
    email: "",
    password: "",
    full_name: "",
    role: "employee",
  });
  const [editUserForm, setEditUserForm] = useState({
    id: "",
    email: "",
    password: "",
    full_name: "",
    role: "employee",
  });

  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userSortBy, setUserSortBy] = useState<
    "id" | "name" | "email" | "role" | "created"
  >("id");
  const [userSortOrder, setUserSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { users, loadUsers, handleAddUser, handleEditUser, handleDeleteUser } =
    useUsers(setError, setSuccess, setLoading);

  const {
    publications,
    myPayslips,
    selectedPublication,
    showPublicationDetails,
    setShowPublicationDetails,
    deletingPublicationId,
    loadPublications,
    loadMyPayslips,
    handleViewPublication,
    handleDeletePublication,
    handleDownloadMyPayslip,
  } = usePublications(setError, setSuccess);

  const loadWorklog = async () => {
    try {
      const r = await api.get("/worklog/publications");
      setWorklogPublications(r.data.publications);
    } catch {}
  };

  const handleViewWorklogPublication = async (id: string) => {
    try {
      const r = await api.get(`/worklog/publications/${id}`);
      setSelectedWorklogPub(r.data);
      setShowWorklogDetails(true);
    } catch {
      setError("Failed to load publication details");
    }
  };

  const handleDeleteWorklogPublication = async (id: string) => {
    if (
      !window.confirm(
        "Delete all work logs from this publication? This cannot be undone.",
      )
    )
      return;
    setDeletingWorklogPubId(id);
    try {
      const r = await api.delete(`/worklog/publications/${id}`);
      const { jobId } = r.data;
      const poll = async (): Promise<void> => {
        const jr = await api.get(`/worklog/jobs/${jobId}`);
        if (jr.data.status === "done") {
          setSuccess(jr.data.result?.message || "Publication deleted");
          setDeletingWorklogPubId(null);
          loadWorklog();
        } else if (jr.data.status === "failed") {
          setError(jr.data.error || "Deletion failed");
          setDeletingWorklogPubId(null);
        } else setTimeout(poll, 2000);
      };
      setTimeout(poll, 1000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete");
      setDeletingWorklogPubId(null);
    }
  };

  const loadForm106 = async () => {
    try {
      const r = await api.get("/form106/publications");
      setForm106Publications(r.data.publications);
    } catch {
      // silently fail
    }
  };

  const handleViewForm106Publication = async (id: string) => {
    try {
      const r = await api.get(`/form106/publications/${id}`);
      setSelectedForm106Pub(r.data);
      setShowForm106Details(true);
    } catch {
      setError("Failed to load publication details");
    }
  };

  const handleReset2FA = async (userId: number, userName: string) => {
    if (
      !window.confirm(
        `Reset 2FA for ${userName}? They will need to set it up again on next login.`,
      )
    )
      return;
    try {
      const r = await api.post(`/2fa/admin-reset/${userId}`);
      setSuccess(r.data.message);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to reset 2FA");
    }
  };

  const handleReset2FAAuthMode = async (userId: number, userName: string) => {
    if (
      !window.confirm(
        `Reset authenticator for ${userName}? They will be prompted to set it up again on next login.`,
      )
    )
      return;
    try {
      const r = await api.post(`/2fa/admin-reset/${userId}`);
      setSuccess(r.data.message);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to reset authenticator");
    }
  };

  const handleRevealPassword = async (userId: number) => {
    if (revealedPasswords[userId] !== undefined) {
      // toggle off
      setRevealedPasswords((prev) => {
        const n = { ...prev };
        delete n[userId];
        return n;
      });
      return;
    }
    try {
      const r = await api.get(`/users/${userId}/initial-password`);
      setRevealedPasswords((prev) => ({
        ...prev,
        [userId]: r.data.initial_password,
      }));
    } catch {
      setError("Failed to fetch initial password");
    }
  };

  const handleResetToInitialPassword = async (
    userId: number,
    userName: string,
  ) => {
    if (
      !window.confirm(
        `Reset password for ${userName}? A new random password will be generated and they will be required to change it on next login.`,
      )
    )
      return;
    setResettingPasswordId(userId);
    try {
      const r = await api.post(`/users/${userId}/reset-password`);
      setSuccess(`Password reset for ${userName}: ${r.data.initial_password}`);
      // Update revealed password cache with new one
      setRevealedPasswords((prev) => ({
        ...prev,
        [userId]: r.data.initial_password,
      }));
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to reset password");
    } finally {
      setResettingPasswordId(null);
    }
  };

  const handleDeleteForm106Publication = async (id: string) => {
    if (
      !window.confirm(
        "Delete all Form 106s from this publication? This cannot be undone.",
      )
    )
      return;
    setDeletingForm106PubId(id);
    try {
      const r = await api.delete(`/form106/publications/${id}`);
      const { jobId } = r.data;
      const poll = async (): Promise<void> => {
        const jr = await api.get(`/form106/delete-jobs/${jobId}`);
        if (jr.data.status === "done") {
          setSuccess(jr.data.result?.message || "Publication deleted");
          setDeletingForm106PubId(null);
          loadForm106();
          loadWorklog();
        } else if (jr.data.status === "failed") {
          setError(jr.data.error || "Deletion failed");
          setDeletingForm106PubId(null);
        } else {
          setTimeout(poll, 2000);
        }
      };
      setTimeout(poll, 1000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete");
      setDeletingForm106PubId(null);
    }
  };

  useEffect(() => {
    loadUsers();
    loadPublications();
    loadMyPayslips();
    loadForm106();
    loadWorklog();
    api
      .get("/audit-log/summary")
      .then((r) =>
        setAlertCount(
          (r.data.alerts || 0) + (r.data.suspiciousActivity24h || 0),
        ),
      )
      .catch(() => {});
    api.get("/2fa/status").then((r) => setTwoFAEnabled(r.data.enabled));
    getAuthMode().then(setAuthMode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [userSearch, userRoleFilter, userSortBy, userSortOrder]);

  const filteredUsers = (() => {
    let list = [...users];
    if (userSearch.trim()) {
      const s = userSearch.toLowerCase();
      list = list.filter(
        (u) =>
          u.full_name.toLowerCase().includes(s) ||
          u.id.toString().includes(s) ||
          (u.email && u.email.toLowerCase().includes(s)),
      );
    }
    if (userRoleFilter !== "all")
      list = list.filter((u) => u.role === userRoleFilter);
    list.sort((a, b) => {
      let av: any, bv: any;
      switch (userSortBy) {
        case "id":
          av = a.id;
          bv = b.id;
          break;
        case "name":
          av = a.full_name.toLowerCase();
          bv = b.full_name.toLowerCase();
          break;
        case "email":
          av = (a.email || "").toLowerCase();
          bv = (b.email || "").toLowerCase();
          break;
        case "role":
          av = a.role;
          bv = b.role;
          break;
        case "created":
          av = new Date(a.created_at).getTime();
          bv = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }
      return av < bv
        ? userSortOrder === "asc"
          ? -1
          : 1
        : av > bv
          ? userSortOrder === "asc"
            ? 1
            : -1
          : 0;
    });
    return list;
  })();

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleBatchUploadEmployees = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    if (!batchEmployeeFile) {
      setError("Please select an Excel file");
      setLoading(false);
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", batchEmployeeFile);
      const r = await api.post("/users/batch-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEmployeeUploadResults(r.data);
      setShowBatchUploadEmployeesModal(false);
      setShowEmployeeUploadResults(true);
      setBatchEmployeeFile(null);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to upload employees");
    } finally {
      setLoading(false);
    }
  };

  const openEditUserModal = (user: User) => {
    setEditingUser(user);
    setEditUserForm({
      id: user.id.toString(),
      email: user.email || "",
      password: "",
      full_name: user.full_name,
      role: user.role,
    });
    setShowEditUserModal(true);
  };

  const submitAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      await handleAddUser(newUser);
      setShowAddUserModal(false);
      setNewUser({
        id: "",
        email: "",
        password: "",
        full_name: "",
        role: "employee",
      });
    } catch {}
  };

  const submitEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setError("");
    setSuccess("");
    try {
      await handleEditUser(editingUser, editUserForm);
      setShowEditUserModal(false);
      setEditingUser(null);
    } catch {}
  };

  const PayslipCard = ({ payslip }: { payslip: Payslip }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <Stack gap={2}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6 className="mb-1">
                {formatMonth(payslip.month)} {payslip.year}
              </h6>
              <small className="text-muted d-block">{payslip.file_name}</small>
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                handleDownloadMyPayslip(payslip.id, payslip.file_name)
              }
            >
              📥 {t("download")}
            </Button>
          </div>
          <small className="text-muted">
            {t("uploaded")}:{" "}
            {new Date(payslip.uploaded_at).toLocaleDateString()}
          </small>
        </Stack>
      </Card.Body>
    </Card>
  );

  const PublicationCard = ({ publication }: { publication: Publication }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <Stack gap={2}>
          <div>
            <h6 className="mb-1">
              {formatMonth(publication.month)} {publication.year}
            </h6>
            <small className="text-muted d-block">
              {new Date(publication.timestamp).toLocaleString()}
            </small>
          </div>
          <div>
            <Badge bg="info" className="me-2">
              {publication.page_count} pages
            </Badge>
            <Badge bg="success" className="me-2">
              {publication.successful_count} ✓
            </Badge>
            {publication.page_count !== publication.successful_count && (
              <Badge bg="warning">
                {publication.page_count - publication.successful_count} failed
              </Badge>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => handleViewPublication(publication.id)}
              className="flex-grow-1"
            >
              {t("viewDetails")}
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDeletePublication(publication.id)}
              disabled={deletingPublicationId === publication.id}
            >
              {deletingPublicationId === publication.id ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("deleting")}
                </>
              ) : (
                t("delete")
              )}
            </Button>
          </div>
        </Stack>
      </Card.Body>
    </Card>
  );

  const UserCard = ({ user }: { user: User }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <Stack gap={2}>
          <div>
            <div className="d-flex justify-content-between align-items-start mb-1">
              <h6 className="mb-0">{user.full_name}</h6>
              <Badge bg={user.role === "admin" ? "success" : "primary"}>
                {user.role}
              </Badge>
            </div>
            <small className="text-muted d-block">ID: {user.id}</small>
            <small className="text-muted d-block">{user.email}</small>
            <small className="text-muted d-block">
              {t("joined")}: {new Date(user.created_at).toLocaleDateString()}
            </small>
          </div>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => openEditUserModal(user)}
              className="flex-grow-1"
            >
              {t("edit")}
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDeleteUser(user.id)}
              disabled={user.role === "admin"}
              className="flex-grow-1"
            >
              {t("delete")}
            </Button>
          </div>
          <div className="d-flex align-items-center gap-2 mt-1">
            <small className="text-muted fw-semibold">
              {t("initialPassword")}:
            </small>
            {revealedPasswords[user.id] !== undefined ? (
              <>
                <code
                  style={{
                    fontSize: 12,
                    background: "#f8f9fa",
                    padding: "1px 5px",
                    borderRadius: 3,
                  }}
                >
                  {revealedPasswords[user.id] ?? "—"}
                </code>
                <button
                  type="button"
                  onClick={() => handleRevealPassword(user.id)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#6c757d",
                    padding: 0,
                  }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                </button>
                {revealedPasswords[user.id] && (
                  <button
                    type="button"
                    onClick={() =>
                      copyToClipboard(revealedPasswords[user.id]!, user.id)
                    }
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      color: copiedId === user.id ? "#198754" : "#6c757d",
                    }}
                    title={copiedId === user.id ? "Copied!" : "Copy"}
                  >
                    {copiedId === user.id ? (
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          x="9"
                          y="9"
                          width="13"
                          height="13"
                          rx="2"
                          ry="2"
                        />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => handleRevealPassword(user.id)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: 0,
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            )}
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            className="w-100 mt-1"
            disabled={resettingPasswordId === user.id}
            onClick={() =>
              handleResetToInitialPassword(user.id, user.full_name)
            }
          >
            {resettingPasswordId === user.id ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              t("resetPassword")
            )}
          </Button>
          {authMode === "password" && user.twofa_enabled && (
            <Button
              size="sm"
              variant="outline-warning"
              onClick={() => handleReset2FA(user.id, user.full_name)}
              className="w-100"
            >
              {t("reset2FA")}
            </Button>
          )}
          {authMode === "authenticator" && (
            <Button
              size="sm"
              variant="outline-warning"
              onClick={() => handleReset2FAAuthMode(user.id, user.full_name)}
              className="w-100"
            >
              {t("resetAuthenticator")}
            </Button>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="py-3 px-2 px-md-4">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-2">{t("adminDashboard")}</h3>
          <p className="text-muted small mb-3">{t("adminDashboardSubtitle")}</p>
          <Alert variant="info" className="small">
            <strong>🔒 {t("securityNotice")}:</strong> {t("securityNoticeText")}{" "}
            {t("myPayslips")} {t("securityNoticeText2")}
          </Alert>
        </Col>
      </Row>

      {authMode === "password" && (
        <>
          <TwoFAButton
            enabled={twoFAEnabled}
            onClick={() =>
              twoFAEnabled
                ? setShowDisable2FAModal(true)
                : setShow2FAModal(true)
            }
            className="mb-3"
          />
          <TwoFASetupModal
            show={show2FAModal}
            onHide={() => setShow2FAModal(false)}
            onSuccess={() => {
              setTwoFAEnabled(true);
              setSuccess("2FA enabled! Save your backup codes.");
            }}
          />
          <TwoFADisableModal
            show={showDisable2FAModal}
            onHide={() => setShowDisable2FAModal(false)}
            onSuccess={() => {
              setTwoFAEnabled(false);
              setSuccess("2FA disabled.");
            }}
          />
        </>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Tabs defaultActiveKey="my-payslips" className="mb-3">
        {/* My Payslips */}
        <Tab
          eventKey="my-payslips"
          title={
            <span className="small">
              {t("myPayslips")}{" "}
              <Badge bg="secondary">{myPayslips.length}</Badge>
            </span>
          }
        >
          <Card>
            <Card.Body className="p-2 p-md-3">
              <h5 className="mb-2">{t("myPayslips")}</h5>
              <p className="text-muted small mb-3">{t("myPayslipsPersonal")}</p>
              <div className="d-none d-lg-block">
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>{t("monthLabel")}</th>
                      <th>{t("year")}</th>
                      <th>{t("fileName")}</th>
                      <th>{t("uploaded")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myPayslips.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          {t("noPayslipsYet")}
                        </td>
                      </tr>
                    ) : (
                      myPayslips.map((p) => (
                        <tr key={p.id}>
                          <td>{formatMonth(p.month)}</td>
                          <td>{p.year}</td>
                          <td>
                            <small className="text-muted">{p.file_name}</small>
                          </td>
                          <td>
                            <small>
                              {new Date(p.uploaded_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() =>
                                handleDownloadMyPayslip(p.id, p.file_name)
                              }
                            >
                              📥 {t("download")}
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="d-lg-none">
                {myPayslips.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    {t("noPayslipsYet")}
                  </div>
                ) : (
                  myPayslips.map((p) => <PayslipCard key={p.id} payslip={p} />)
                )}
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Publications */}
        <Tab
          eventKey="publications"
          title={
            <span className="small">
              {t("publications")}{" "}
              <Badge bg="secondary">{publications.length}</Badge>
            </span>
          }
        >
          <Card>
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="mb-1">{t("payslipPublications")}</h5>
                  <small className="text-muted d-block">
                    {t("managePublications")}
                  </small>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowBatchUploadModal(true)}
                >
                  📄 {t("upload")}
                </Button>
              </div>
              <div className="d-none d-lg-block">
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>
                        {t("monthLabel")}/{t("year")}
                      </th>
                      <th>{t("uploaded")}</th>
                      <th>{t("totalPages")}</th>
                      <th>{t("successful")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publications.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          {t("noPublicationsYet")}
                        </td>
                      </tr>
                    ) : (
                      publications.map((pub) => (
                        <tr key={pub.id}>
                          <td>
                            <strong>
                              {formatMonth(pub.month)} {pub.year}
                            </strong>
                          </td>
                          <td>
                            <small>
                              {new Date(pub.timestamp).toLocaleString()}
                            </small>
                          </td>
                          <td>{pub.page_count}</td>
                          <td>
                            <Badge bg="success">{pub.successful_count}</Badge>
                            {pub.page_count !== pub.successful_count && (
                              <Badge bg="warning" className="ms-2">
                                {pub.page_count - pub.successful_count} failed
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleViewPublication(pub.id)}
                              className="me-2"
                            >
                              {t("viewDetails")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeletePublication(pub.id)}
                              disabled={deletingPublicationId === pub.id}
                            >
                              {deletingPublicationId === pub.id ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  {t("deleting")}
                                </>
                              ) : (
                                t("delete")
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="d-lg-none">
                {publications.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    {t("noPublicationsYet")}
                  </div>
                ) : (
                  publications.map((pub) => (
                    <PublicationCard key={pub.id} publication={pub} />
                  ))
                )}
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Users */}
        <Tab
          eventKey="users"
          title={
            <span className="small">
              {t("users")} <Badge bg="secondary">{filteredUsers.length}</Badge>
            </span>
          }
        >
          <Card>
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0">{t("manageUsers")}</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setShowMassDeleteModal(true)}
                  >
                    🗑 {t("massDelete")}
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowBatchUploadEmployeesModal(true)}
                  >
                    📊 {t("batchUpload")}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowAddUserModal(true)}
                  >
                    + {t("addUser")}
                  </Button>
                </div>
              </div>

              <Row className="mb-3 g-2">
                <Col xs={12} md={6} lg={4}>
                  <Form.Control
                    type="text"
                    placeholder={`🔍 ${t("searchPlaceholder")}`}
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col xs={6} md={3} lg={2}>
                  <Form.Select
                    size="sm"
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value)}
                  >
                    <option value="all">{t("allRoles")}</option>
                    <option value="admin">{t("admin")}</option>
                    <option value="employee">{t("employee")}</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md={3} lg={2}>
                  <Form.Select
                    size="sm"
                    value={userSortBy}
                    onChange={(e) => setUserSortBy(e.target.value as any)}
                  >
                    <option value="id">{t("sortById")}</option>
                    <option value="name">{t("sortByName")}</option>
                    <option value="email">{t("sortByEmail")}</option>
                    <option value="role">{t("sortByRole")}</option>
                    <option value="created">{t("sortByDate")}</option>
                  </Form.Select>
                </Col>
                <Col xs={12} lg={4} className="d-flex gap-2 align-items-center">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() =>
                      setUserSortOrder(userSortOrder === "asc" ? "desc" : "asc")
                    }
                    style={{ minWidth: "80px" }}
                  >
                    {userSortOrder === "asc" ? "↑ Asc" : "↓ Desc"}
                  </Button>
                  {(userSearch || userRoleFilter !== "all") && (
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => {
                        setUserSearch("");
                        setUserRoleFilter("all");
                      }}
                      className="text-decoration-none"
                    >
                      {t("clearFilters")}
                    </Button>
                  )}
                  <small className="text-muted ms-auto">
                    {t("showing")} {paginatedUsers.length} {t("of")}{" "}
                    {filteredUsers.length}
                  </small>
                </Col>
              </Row>

              {filteredUsers.length === 0 &&
                (userSearch || userRoleFilter !== "all") && (
                  <Alert variant="info" className="mb-3">
                    {t("noUsersMatch")}
                  </Alert>
                )}

              <div className="d-none d-lg-block">
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>{t("fullName")}</th>
                      <th>{t("email")}</th>
                      <th>{t("role")}</th>
                      <th>{t("createdAt")}</th>
                      <th>{t("initialPassword")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          {users.length === 0
                            ? t("noUsersYet")
                            : t("noUsersMatch")}
                        </td>
                      </tr>
                    ) : (
                      paginatedUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <strong>{user.id}</strong>
                          </td>
                          <td>{user.full_name}</td>
                          <td>
                            <small className="text-muted">
                              {user.email || "—"}
                            </small>
                          </td>
                          <td>
                            <Badge
                              bg={user.role === "admin" ? "success" : "primary"}
                            >
                              {user.role}
                            </Badge>
                          </td>
                          <td>
                            <small>
                              {new Date(user.created_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {revealedPasswords[user.id] !== undefined ? (
                                <>
                                  <code
                                    style={{
                                      fontSize: 13,
                                      letterSpacing: "0.05em",
                                      background: "#f8f9fa",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                    }}
                                  >
                                    {revealedPasswords[user.id] ?? "—"}
                                  </code>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRevealPassword(user.id)
                                    }
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      cursor: "pointer",
                                      color: "#6c757d",
                                      padding: "2px 4px",
                                    }}
                                    title="Hide"
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                      <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                  </button>
                                  {revealedPasswords[user.id] && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyToClipboard(
                                          revealedPasswords[user.id]!,
                                          user.id,
                                        )
                                      }
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        padding: "2px 4px",
                                        color:
                                          copiedId === user.id
                                            ? "#198754"
                                            : "#6c757d",
                                      }}
                                      title={
                                        copiedId === user.id
                                          ? "Copied!"
                                          : "Copy password"
                                      }
                                    >
                                      {copiedId === user.id ? (
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      ) : (
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <rect
                                            x="9"
                                            y="9"
                                            width="13"
                                            height="13"
                                            rx="2"
                                            ry="2"
                                          />
                                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                        </svg>
                                      )}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleRevealPassword(user.id)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    color: "#6c757d",
                                    padding: "2px 4px",
                                  }}
                                  title="Show initial password"
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => openEditUserModal(user)}
                              >
                                {t("edit")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={user.role === "admin"}
                              >
                                {t("delete")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                disabled={resettingPasswordId === user.id}
                                onClick={() =>
                                  handleResetToInitialPassword(
                                    user.id,
                                    user.full_name,
                                  )
                                }
                                title="Generate new random password and require change on next login"
                              >
                                {resettingPasswordId === user.id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  t("resetPassword")
                                )}
                              </Button>
                              {authMode === "password" &&
                                user.twofa_enabled && (
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    onClick={() =>
                                      handleReset2FA(user.id, user.full_name)
                                    }
                                    title="Reset 2FA — use if employee lost their phone"
                                  >
                                    {t("reset2FA")}
                                  </Button>
                                )}
                              {authMode === "authenticator" && (
                                <Button
                                  size="sm"
                                  variant="outline-warning"
                                  onClick={() =>
                                    handleReset2FAAuthMode(
                                      user.id,
                                      user.full_name,
                                    )
                                  }
                                  title="Reset authenticator — use if employee lost their phone"
                                >
                                  {t("resetAuthenticator")}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="d-lg-none">
                {paginatedUsers.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    {users.length === 0 ? t("noUsersYet") : t("noUsersMatch")}
                  </div>
                ) : (
                  paginatedUsers.map((user) => (
                    <UserCard key={user.id} user={user} />
                  ))
                )}
              </div>

              {totalPages > 1 && (
                <Row className="mt-3">
                  <Col className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">{t("itemsPerPage")}:</small>
                      <Form.Select
                        size="sm"
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        style={{ width: "80px" }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        ««
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ‹
                      </Button>
                      <span className="px-3 py-1 small d-flex align-items-center">
                        {t("pageLabel")} {currentPage} {t("of")} {totalPages}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        ›
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        »»
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Polls */}
        <Tab
          eventKey="polls"
          title={<span className="small">📊 {t("polls")}</span>}
        >
          <AdminPolls />
        </Tab>

        {/* Form 106 */}
        <Tab
          eventKey="form106"
          title={
            <span className="small">
              📋 {t("form106Tab")}
              {form106Publications.length > 0 && (
                <Badge bg="secondary" className="ms-1">
                  {form106Publications.length}
                </Badge>
              )}
            </span>
          }
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-2 p-md-3">
              <Tabs
                defaultActiveKey="f106-admin"
                className="mb-3"
                variant="pills"
              >
                {/* Upload & Publications */}
                <Tab
                  eventKey="f106-admin"
                  title={<span className="small">⬆️ {t("uploadAndView")}</span>}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                    <h6 className="mb-0 fw-semibold">{t("form106Title")}</h6>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowForm106UploadModal(true)}
                    >
                      ⬆️ {t("uploadForm106")}
                    </Button>
                  </div>
                  {form106Publications.length === 0 ? (
                    <p className="text-muted small text-center py-3">
                      {t("noForm106Yet")}
                    </p>
                  ) : (
                    <>
                      <div className="d-none d-md-block">
                        <Table responsive hover size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>{t("year")}</th>
                              <th>{t("uploaded")}</th>
                              <th>{t("pages")}</th>
                              <th>{t("successful")}</th>
                              <th>{t("actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {form106Publications.map((pub) => (
                              <tr key={pub.id}>
                                <td>
                                  <strong>{pub.year}</strong>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {new Date(
                                      pub.timestamp,
                                    ).toLocaleDateString()}
                                  </small>
                                </td>
                                <td>{pub.page_count}</td>
                                <td>
                                  <Badge bg="success" className="me-1">
                                    {pub.successful_count}
                                  </Badge>
                                  {pub.page_count !== pub.successful_count && (
                                    <Badge bg="warning">
                                      {pub.page_count - pub.successful_count} ❌
                                    </Badge>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() =>
                                        handleViewForm106Publication(pub.id)
                                      }
                                    >
                                      {t("viewDetails")}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      disabled={deletingForm106PubId === pub.id}
                                      onClick={() =>
                                        handleDeleteForm106Publication(pub.id)
                                      }
                                    >
                                      {deletingForm106PubId === pub.id ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        t("delete")
                                      )}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <div className="d-md-none">
                        {form106Publications.map((pub) => (
                          <div key={pub.id} className="border rounded p-2 mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong>{pub.year}</strong>
                              <small className="text-muted">
                                {new Date(pub.timestamp).toLocaleDateString()}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <Badge bg="success">{pub.successful_count}</Badge>
                              <small className="text-muted">
                                / {pub.page_count} {t("pages")}
                              </small>
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="flex-grow-1"
                                onClick={() =>
                                  handleViewForm106Publication(pub.id)
                                }
                              >
                                {t("viewDetails")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="flex-grow-1"
                                disabled={deletingForm106PubId === pub.id}
                                onClick={() =>
                                  handleDeleteForm106Publication(pub.id)
                                }
                              >
                                {t("delete")}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Tab>

                {/* My Form 106 */}
                <Tab
                  eventKey="f106-mine"
                  title={<span className="small">👤 {t("myForm106Tab")}</span>}
                >
                  <div className="mt-2">
                    <EmployeeForm106 />
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Tab>

        {/* Work Log */}
        <Tab
          eventKey="worklog"
          title={
            <span className="small">
              📋 {t("worklogTab")}
              {worklogPublications.length > 0 && (
                <Badge bg="secondary" className="ms-1">
                  {worklogPublications.length}
                </Badge>
              )}
            </span>
          }
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-2 p-md-3">
              <Tabs
                defaultActiveKey="wl-admin"
                className="mb-3"
                variant="pills"
              >
                {/* Upload & Publications */}
                <Tab
                  eventKey="wl-admin"
                  title={<span className="small">⬆️ {t("uploadAndView")}</span>}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                    <h6 className="mb-0 fw-semibold">{t("worklogTitle")}</h6>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowWorklogUploadModal(true)}
                    >
                      ⬆️ {t("uploadWorklog")}
                    </Button>
                  </div>
                  {worklogPublications.length === 0 ? (
                    <p className="text-muted small text-center py-3">
                      {t("noWorklogYet")}
                    </p>
                  ) : (
                    <>
                      <div className="d-none d-md-block">
                        <Table responsive hover size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>
                                {t("month")}/{t("year")}
                              </th>
                              <th>{t("uploaded")}</th>
                              <th>{t("pages")}</th>
                              <th>{t("successful")}</th>
                              <th>{t("actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {worklogPublications.map((pub) => (
                              <tr key={pub.id}>
                                <td>
                                  <strong>
                                    {pub.month}/{pub.year}
                                  </strong>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {new Date(
                                      pub.timestamp,
                                    ).toLocaleDateString()}
                                  </small>
                                </td>
                                <td>{pub.page_count}</td>
                                <td>
                                  <Badge bg="success" className="me-1">
                                    {pub.successful_count}
                                  </Badge>
                                  {pub.failed_count > 0 && (
                                    <Badge bg="warning">
                                      {pub.failed_count} ❌
                                    </Badge>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() =>
                                        handleViewWorklogPublication(pub.id)
                                      }
                                    >
                                      {t("viewDetails")}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      disabled={deletingWorklogPubId === pub.id}
                                      onClick={() =>
                                        handleDeleteWorklogPublication(pub.id)
                                      }
                                    >
                                      {deletingWorklogPubId === pub.id ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        t("delete")
                                      )}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <div className="d-md-none">
                        {worklogPublications.map((pub) => (
                          <div key={pub.id} className="border rounded p-2 mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong>
                                {pub.month}/{pub.year}
                              </strong>
                              <small className="text-muted">
                                {new Date(pub.timestamp).toLocaleDateString()}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <Badge bg="success">{pub.successful_count}</Badge>
                              <small className="text-muted">
                                / {pub.page_count} {t("pages")}
                              </small>
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="flex-grow-1"
                                onClick={() =>
                                  handleViewWorklogPublication(pub.id)
                                }
                              >
                                {t("viewDetails")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="flex-grow-1"
                                disabled={deletingWorklogPubId === pub.id}
                                onClick={() =>
                                  handleDeleteWorklogPublication(pub.id)
                                }
                              >
                                {t("delete")}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Tab>

                {/* My Work Log */}
                <Tab
                  eventKey="wl-mine"
                  title={<span className="small">👤 {t("myWorklogTab")}</span>}
                >
                  <div className="mt-2">
                    <EmployeeWorklog />
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Tab>

        {/* Logs & Security */}
        <Tab
          eventKey="logs"
          title={
            <span className="small">
              🔒 {t("logsTab")}
              {alertCount > 0 && (
                <Badge bg="danger" className="ms-1">
                  {alertCount}
                </Badge>
              )}
            </span>
          }
        >
          <Card>
            <Card.Body className="p-2 p-md-3">
              <h5 className="mb-1">{t("logsTitle")}</h5>
              <small className="text-muted d-block mb-3">
                {t("logsSubtitle")}
              </small>
              <AdminLogs />
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Mass Delete — self-contained, owns all its state */}
      <MassDeleteModal
        show={showMassDeleteModal}
        onHide={() => setShowMassDeleteModal(false)}
        onSuccess={loadUsers}
      />

      {/* Batch Upload Payslips */}
      {/* Batch Upload Payslips — self-contained async modal */}
      <BatchUploadModal
        show={showBatchUploadModal}
        onHide={() => setShowBatchUploadModal(false)}
        onComplete={() => {
          loadPublications();
          loadMyPayslips();
        }}
      />

      <Form106UploadModal
        show={showForm106UploadModal}
        onHide={() => setShowForm106UploadModal(false)}
        onComplete={loadForm106}
      />

      <WorklogUploadModal
        show={showWorklogUploadModal}
        onHide={() => setShowWorklogUploadModal(false)}
        onComplete={loadWorklog}
      />

      {/* Worklog Publication Details Modal */}
      <Modal
        show={showWorklogDetails}
        onHide={() => setShowWorklogDetails(false)}
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            📋 {t("worklogTitle")} {selectedWorklogPub?.month}/
            {selectedWorklogPub?.year}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedWorklogPub && (
            <>
              <div className="d-flex gap-3 mb-3 flex-wrap">
                <Badge bg="success" style={{ fontSize: 13 }}>
                  ✅ {selectedWorklogPub.successful_count} {t("successful")}
                </Badge>
                {selectedWorklogPub.failed_count > 0 && (
                  <Badge bg="danger" style={{ fontSize: 13 }}>
                    ❌ {selectedWorklogPub.failed_count} failed
                  </Badge>
                )}
              </div>
              {selectedWorklogPub.records?.length > 0 && (
                <Table size="sm" hover className="mb-3">
                  <thead className="table-light">
                    <tr>
                      <th>{t("employeeId")}</th>
                      <th>{t("fullName")}</th>
                      <th>{t("fileName")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedWorklogPub.records.map((r: any) => (
                      <tr key={r.id}>
                        <td>{r.user_id}</td>
                        <td>{r.user_name}</td>
                        <td>
                          <small className="text-muted">{r.file_name}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {selectedWorklogPub.failed_pages?.length > 0 && (
                <>
                  <h6 className="small fw-bold mb-2">
                    ❌ {t("failedPagesList")}
                  </h6>
                  {selectedWorklogPub.failed_pages.map((fp: any, i: number) => (
                    <div
                      key={i}
                      className="small py-1 border-bottom text-danger"
                    >
                      {t("pageOf")}{" "}
                      {Array.isArray(fp.pages) ? fp.pages.join(", ") : fp.page}{" "}
                      — {fp.error}
                      {fp.employeeNumber ? ` (#${fp.employeeNumber})` : ""}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowWorklogDetails(false)}
          >
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Background jobs floating status bar */}
      <BackgroundJobsBar
        onJobComplete={(job) => {
          // Reload the relevant tab's data when a background job finishes
          if (job.type === "form106") loadForm106();
          if (job.type === "worklog") loadWorklog();
        }}
      />

      {/* Form 106 Publication Details Modal */}
      <Modal
        show={showForm106Details}
        onHide={() => setShowForm106Details(false)}
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            📋 {t("form106Title")} {selectedForm106Pub?.year} —{" "}
            {t("viewDetails")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedForm106Pub && (
            <>
              <div className="d-flex gap-3 mb-3 flex-wrap">
                <Badge bg="success" style={{ fontSize: 13 }}>
                  ✅ {selectedForm106Pub.successful_count} {t("successful")}
                </Badge>
                {selectedForm106Pub.failed_count > 0 && (
                  <Badge bg="danger" style={{ fontSize: 13 }}>
                    ❌ {selectedForm106Pub.failed_count} {t("failedPages")}
                  </Badge>
                )}
                <Badge bg="secondary" style={{ fontSize: 13 }}>
                  {selectedForm106Pub.page_count} {t("pages")}
                </Badge>
              </div>
              {selectedForm106Pub.forms?.length > 0 && (
                <>
                  <h6 className="small fw-bold mb-2">
                    ✅ {t("successfulPages")}
                  </h6>
                  <Table size="sm" hover className="mb-3">
                    <thead className="table-light">
                      <tr>
                        <th>{t("employeeId")}</th>
                        <th>{t("fullName")}</th>
                        <th>{t("fileName")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedForm106Pub.forms.map((f: any) => (
                        <tr key={f.id}>
                          <td>{f.user_id}</td>
                          <td>{f.user_name}</td>
                          <td>
                            <small className="text-muted">{f.file_name}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
              {selectedForm106Pub.failed_pages?.length > 0 && (
                <>
                  <h6 className="small fw-bold mb-2">
                    ❌ {t("failedPagesList")}
                  </h6>
                  {selectedForm106Pub.failed_pages.map((fp: any, i: number) => (
                    <div
                      key={i}
                      className="small py-1 border-bottom text-danger"
                    >
                      {t("pageOf")} {fp.page} — {fp.error}{" "}
                      {fp.employeeNumber && `(#${fp.employeeNumber})`}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowForm106Details(false)}
          >
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Publication Details */}
      <Modal
        show={showPublicationDetails}
        onHide={() => setShowPublicationDetails(false)}
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">{t("publicationDetails")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPublication && (
            <>
              <div className="mb-3">
                <h5>
                  {formatMonth(selectedPublication.month)}{" "}
                  {selectedPublication.year}
                </h5>
                <p className="text-muted small mb-2">
                  {t("uploaded")}:{" "}
                  {new Date(selectedPublication.timestamp).toLocaleString()}
                </p>
                <div>
                  <Badge bg="info" className="me-2">
                    {selectedPublication.page_count} pages
                  </Badge>
                  <Badge bg="success" className="me-2">
                    {selectedPublication.successful_count} successful
                  </Badge>
                  {selectedPublication.failed_count > 0 && (
                    <Badge bg="danger">
                      {selectedPublication.failed_count} failed
                    </Badge>
                  )}
                </div>
              </div>
              {selectedPublication.failed_pages &&
                selectedPublication.failed_pages.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-danger small fw-bold">
                      ❌ Failed Pages ({selectedPublication.failed_pages.length}
                      )
                    </h6>
                    <ListGroup className="small">
                      {selectedPublication.failed_pages.map((f, i) => (
                        <ListGroup.Item key={i} variant="danger">
                          <strong>Page {f.page}:</strong> {f.error}
                          {f.employeeNumber &&
                            ` (Employee #${f.employeeNumber})`}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              <h6 className="small fw-bold">
                ✅ Payslips Created ({selectedPublication.payslips.length})
              </h6>
              <div className="d-none d-md-block">
                <Table responsive striped className="small">
                  <thead>
                    <tr>
                      <th>{t("employeeId")}</th>
                      <th>{t("fullName")}</th>
                      <th>{t("email")}</th>
                      <th>{t("fileName")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPublication.payslips.map((p) => (
                      <tr key={p.id}>
                        <td>{p.user_id}</td>
                        <td>{p.user_name}</td>
                        <td>{p.user_email}</td>
                        <td>
                          <small>{p.file_name}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="d-md-none">
                {selectedPublication.payslips.map((p) => (
                  <Card key={p.id} className="mb-2">
                    <Card.Body className="p-2 small">
                      <div>
                        <strong>ID:</strong> {p.user_id}
                      </div>
                      <div>
                        <strong>Name:</strong> {p.user_name}
                      </div>
                      <div className="text-truncate">
                        <strong>Email:</strong> {p.user_email}
                      </div>
                      <div className="text-muted text-truncate">
                        {p.file_name}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPublicationDetails(false)}
          >
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Batch Upload Employees */}
      <Modal
        show={showBatchUploadEmployeesModal}
        onHide={() => setShowBatchUploadEmployeesModal(false)}
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">{t("batchUploadEmployees")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small mb-3">
            <strong>📊 Excel / CSV File Upload</strong>
            <p className="mb-2 mt-2">
              Upload an Excel (.xlsx, .xls) or CSV (.csv) file with these
              columns:
            </p>
            <ul className="mb-0 small">
              <li>
                <strong>Employee Number</strong> (מספר עובד) - Required
              </li>
              <li>
                <strong>Full Name</strong> (שם עובד) - Required
              </li>
              <li>
                <strong>Email</strong> (אימייל) - Optional
              </li>
              <li>
                <strong>ID Number</strong> (מספר זהות) - Required
              </li>
              <li>
                <strong>Phone</strong> (טלפון) - Optional
              </li>
            </ul>
            <p className="mb-0 mt-2 text-muted">
              <small>Default password: Employee Number + ID Number</small>
            </p>
          </Alert>
          <Form onSubmit={handleBatchUploadEmployees}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                Excel or CSV File *
              </Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={(e: any) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (
                    !file.name.endsWith(".xlsx") &&
                    !file.name.endsWith(".xls") &&
                    !file.name.endsWith(".csv")
                  ) {
                    setError(
                      "Only Excel (.xlsx, .xls) or CSV (.csv) files are allowed",
                    );
                    e.target.value = "";
                    return;
                  }
                  if (file.size > 5 * 1024 * 1024) {
                    setError("File size must be less than 5MB");
                    e.target.value = "";
                    return;
                  }
                  setError("");
                  setBatchEmployeeFile(file);
                }}
                required
              />
              {batchEmployeeFile && (
                <div className="mt-2">
                  <small className="text-success">
                    ✓ {batchEmployeeFile.name} (
                    {(batchEmployeeFile.size / 1024).toFixed(2)} KB)
                  </small>
                </div>
              )}
              <Form.Text className="text-muted small">
                Accepts .xlsx, .xls, .csv — Max 5MB.
              </Form.Text>
            </Form.Group>
            <div className="d-flex flex-column-reverse flex-sm-row justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowBatchUploadEmployeesModal(false);
                  setError("");
                  setBatchEmployeeFile(null);
                }}
                disabled={loading}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading || !batchEmployeeFile}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {t("uploading")}
                  </>
                ) : (
                  t("uploadEmployees")
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Employee Upload Results */}
      <Modal
        show={showEmployeeUploadResults}
        onHide={() => setShowEmployeeUploadResults(false)}
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">{t("batchUploadResults")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {employeeUploadResults && (
            <>
              <Alert
                variant={
                  employeeUploadResults.failed === 0 ? "success" : "warning"
                }
                className="mb-3"
              >
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <strong>{t("uploadComplete")}</strong>
                    <p className="mb-0 small mt-1">
                      {employeeUploadResults.message}
                    </p>
                  </div>
                  <div className="text-end">
                    <Badge bg="success" className="me-2">
                      {employeeUploadResults.successful} successful
                    </Badge>
                    {employeeUploadResults.failed > 0 && (
                      <Badge bg="danger">
                        {employeeUploadResults.failed} failed
                      </Badge>
                    )}
                  </div>
                </div>
              </Alert>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>{t("employeeId")} #</th>
                      <th>{t("fullName")}</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeUploadResults.results.map((r: any, i: number) => (
                      <tr
                        key={i}
                        className={r.success ? "table-success" : "table-danger"}
                      >
                        <td>{r.row}</td>
                        <td>{r.employee_number}</td>
                        <td>{r.full_name}</td>
                        <td>
                          {r.success ? (
                            <span className="text-success">✓ Success</span>
                          ) : (
                            <span className="text-danger">✗ {r.error}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowEmployeeUploadResults(false)}
          >
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add User */}
      <Modal
        show={showAddUserModal}
        onHide={() => setShowAddUserModal(false)}
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">{t("addNewUser")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitAddUser}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                {t("employeeId")} *
              </Form.Label>
              <Form.Control
                type="number"
                value={newUser.id}
                onChange={(e) => setNewUser({ ...newUser, id: e.target.value })}
                placeholder="Enter employee ID"
                required
              />
              <Form.Text className="text-muted small">
                {t("employeeIdUnique")}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">{t("fullName")}</Form.Label>
              <Form.Control
                type="text"
                value={newUser.full_name}
                onChange={(e) =>
                  setNewUser({ ...newUser, full_name: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                {t("emailOptional")}
              </Form.Label>
              <Form.Control
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                placeholder={t("leaveEmptyEmail")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">{t("password")}</Form.Label>
              <div
                className="alert alert-info py-2 px-3 small mb-0"
                style={{ borderRadius: 8 }}
              >
                🔑 {t("autoPasswordInfo")}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">{t("role")}</Form.Label>
              <Form.Select
                value={newUser.role}
                onChange={(e) =>
                  setNewUser({ ...newUser, role: e.target.value })
                }
              >
                <option value="employee">{t("employee")}</option>
                <option value="admin">{t("admin")}</option>
              </Form.Select>
            </Form.Group>
            <div className="d-flex flex-column-reverse flex-sm-row justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowAddUserModal(false)}
              >
                {t("cancel")}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? t("loading") : t("addUser")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit User */}
      <Modal
        show={showEditUserModal}
        onHide={() => setShowEditUserModal(false)}
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            {t("editUserTitle")} {editingUser && `(ID: ${editingUser.id})`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitEditUser}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                {t("employeeId")}
              </Form.Label>
              <Form.Control
                type="number"
                value={editUserForm.id || editingUser?.id || ""}
                onChange={(e) =>
                  setEditUserForm({ ...editUserForm, id: e.target.value })
                }
              />
              <Form.Text className="text-muted small">
                {t("changeEmployeeId")}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">{t("fullName")}</Form.Label>
              <Form.Control
                type="text"
                value={editUserForm.full_name}
                onChange={(e) =>
                  setEditUserForm({
                    ...editUserForm,
                    full_name: e.target.value,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                {t("emailOptional")}
              </Form.Label>
              <Form.Control
                type="email"
                value={editUserForm.email || ""}
                onChange={(e) =>
                  setEditUserForm({ ...editUserForm, email: e.target.value })
                }
                placeholder={t("leaveEmptyEmail")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">
                {t("newPasswordOptional")}
              </Form.Label>
              <Form.Control
                type="password"
                value={editUserForm.password}
                onChange={(e) =>
                  setEditUserForm({ ...editUserForm, password: e.target.value })
                }
                placeholder={t("leaveEmptyPassword")}
                minLength={6}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">{t("role")}</Form.Label>
              <Form.Select
                value={editUserForm.role}
                onChange={(e) =>
                  setEditUserForm({ ...editUserForm, role: e.target.value })
                }
              >
                <option value="employee">{t("employee")}</option>
                <option value="admin">{t("admin")}</option>
              </Form.Select>
            </Form.Group>
            <div className="d-flex flex-column-reverse flex-sm-row justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowEditUserModal(false)}
              >
                {t("cancel")}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? t("loading") : t("editUser")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
